import { ContentCompetency, SidebarMenuItem } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  SOLUTION_SESSION_IDS,
  OFFICE_HOURS_SESSION_TYPES,
} from '@backrs_ui/grow-ui/src/models/models';

import { SectionDependencies } from './menuSections.hook';

const MIDDLE_SECTION_TITLES = appStrings.modules.backrsPageShell.menuItems.middle;

const findCompetencySlug = (title: string, competencies?: ContentCompetency[]) =>
  competencies?.filter((competency) => competency.title.toLowerCase() === title.toLowerCase())[0]
    ?.slug;

const academicExpandable = ({
  navigate,
  location,
  competencies,
  isUnauthenticated,
}: Pick<
  SectionDependencies,
  'navigate' | 'location' | 'dispatch' | 'competencies' | 'isUnauthenticated'
>): SidebarMenuItem[] => [
  {
    title: MIDDLE_SECTION_TITLES.academics,
    hidden: isUnauthenticated,
    leftIcon: { iconName: 'GraduationHat02' },
    variant: {
      type: 'EXPANDABLE',
      subItems: [
        {
          title: MIDDLE_SECTION_TITLES.collegeShortList,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive:
              location.pathname ===
              APP_ROUTE.SOLUTION_SESSION.replace(':id?', SOLUTION_SESSION_IDS.COLLEGE_SHORTLIST),
            onClick: () =>
              navigate(
                APP_ROUTE.SOLUTION_SESSION.replace(':id?', SOLUTION_SESSION_IDS.COLLEGE_SHORTLIST),
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.whatAreYourChances,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive:
              location.pathname ===
              APP_ROUTE.OFFICE_HOURS.replace(
                ':id?',
                APP_ROUTE.OFFICE_HOURS.replace(
                  ':id?',
                  OFFICE_HOURS_SESSION_TYPES.WHAT_ARE_YOUR_CHANCES,
                ),
              ),
            onClick: () =>
              navigate(
                APP_ROUTE.OFFICE_HOURS.replace(
                  ':id?',
                  OFFICE_HOURS_SESSION_TYPES.WHAT_ARE_YOUR_CHANCES,
                ),
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.exploreUniversities,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive: location.pathname === `${APP_ROUTE.SEARCH}/universities`,
            onClick: () => navigate(`${APP_ROUTE.SEARCH}/universities`),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.resumeOptimizer,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive:
              location.pathname ===
              APP_ROUTE.OFFICE_HOURS.replace(':id?', OFFICE_HOURS_SESSION_TYPES.RESUME_OPTIMIZER),
            onClick: () =>
              navigate(
                APP_ROUTE.OFFICE_HOURS.replace(':id?', OFFICE_HOURS_SESSION_TYPES.RESUME_OPTIMIZER),
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.majorsAndCourses,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive: location.pathname === APP_ROUTE.COMPETENCY,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Classes, Majors, Degrees', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.improveMyScores,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive:
              location.pathname ===
              APP_ROUTE.SOLUTION_SESSION.replace(
                ':id?',
                SOLUTION_SESSION_IDS.IMPROVE_YOUR_TEST_SCORES,
              ),
            onClick: () =>
              navigate(
                APP_ROUTE.SOLUTION_SESSION.replace(
                  ':id?',
                  SOLUTION_SESSION_IDS.IMPROVE_YOUR_TEST_SCORES,
                ),
              ),
          },
        },
      ],
    },
  },
];

const careersExpandable = ({
  navigate,
  location,
  competencies,
  isUnauthenticated,
}: Pick<
  SectionDependencies,
  'navigate' | 'location' | 'dispatch' | 'competencies' | 'isUnauthenticated'
>): SidebarMenuItem[] => [
  {
    title: MIDDLE_SECTION_TITLES.careers,
    hidden: isUnauthenticated,
    leftIcon: { iconName: 'Briefcase02' },
    variant: {
      type: 'EXPANDABLE',
      subItems: [
        {
          title: MIDDLE_SECTION_TITLES.tracksForYou,
          variant: {
            isActive: location.pathname === APP_ROUTE.CAREER_TRACKS,
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () => navigate(APP_ROUTE.CAREER_TRACKS),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.pathsForYou,
          variant: {
            isActive: location.pathname === APP_ROUTE.CAREER_PATHS,
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () => navigate(APP_ROUTE.CAREER_PATHS),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.internshipsJobs,
          variant: {
            isActive: location.pathname === `${APP_ROUTE.SEARCH}/internships`,
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () => navigate(`${APP_ROUTE.SEARCH}/internships`),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.boostOddsOfAnOffer,
          variant: {
            isActive:
              location.pathname ===
              APP_ROUTE.OFFICE_HOURS.replace(
                ':id?',
                OFFICE_HOURS_SESSION_TYPES.BOOST_ODDS_OF_AN_OFFER,
              ),
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () =>
              navigate(
                APP_ROUTE.OFFICE_HOURS.replace(
                  ':id?',
                  OFFICE_HOURS_SESSION_TYPES.BOOST_ODDS_OF_AN_OFFER,
                ),
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.theInterpreter,
          variant: {
            isActive:
              location.pathname ===
              APP_ROUTE.OFFICE_HOURS.replace(':id?', OFFICE_HOURS_SESSION_TYPES.THE_INTERPRETER),
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () =>
              navigate(
                APP_ROUTE.OFFICE_HOURS.replace(':id?', OFFICE_HOURS_SESSION_TYPES.THE_INTERPRETER),
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.commonThreads,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive:
              location.pathname ===
              APP_ROUTE.SOLUTION_SESSION.replace(':id?', SOLUTION_SESSION_IDS.COMMON_THREADS),
            onClick: () => {
              navigate(
                APP_ROUTE.SOLUTION_SESSION.replace(':id?', SOLUTION_SESSION_IDS.COMMON_THREADS),
              );
            },
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.networking,
          variant: {
            isActive: location.pathname === APP_ROUTE.COMPETENCY,
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () =>
              navigate(`${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Networking', competencies)}`),
          },
        },
      ],
    },
  },
];

const financesExpandable = ({
  navigate,
  location,
  competencies,
  isUnauthenticated,
}: Pick<
  SectionDependencies,
  'dispatch' | 'navigate' | 'location' | 'competencies' | 'isUnauthenticated'
>): SidebarMenuItem[] => [
  {
    title: MIDDLE_SECTION_TITLES.finances,
    hidden: isUnauthenticated,
    leftIcon: { iconName: 'CoinsStacked02' },
    variant: {
      type: 'EXPANDABLE',
      subItems: [
        {
          title: MIDDLE_SECTION_TITLES.extraCash,
          variant: {
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Securing Extra Cash', competencies)}`,
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Securing Extra Cash', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.findScholarships,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive: location.pathname === `${APP_ROUTE.SEARCH}/scholarships`,
            isSubItem: true,
            onClick: () => navigate(`${APP_ROUTE.SEARCH}/scholarships`),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.financialAid,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Financial Aid', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Financial Aid', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.learnAboutMoney,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Money Foundations', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Money Foundations', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.savingsAndInvesting,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Savings & Investing', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Savings & Investing', competencies)}`,
              ),
          },
        },
      ],
    },
  },
];

const healthAndSkillsExpandable = ({
  competencies,
  navigate,
  location,
  isUnauthenticated,
}: Pick<
  SectionDependencies,
  'competencies' | 'navigate' | 'location' | 'isUnauthenticated'
>): SidebarMenuItem[] => [
  {
    title: MIDDLE_SECTION_TITLES.healthAndSkills,
    hidden: isUnauthenticated,
    leftIcon: { iconName: 'ActivityHeart' },
    variant: {
      type: 'EXPANDABLE',
      subItems: [
        {
          title: MIDDLE_SECTION_TITLES.growthMindset,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Growth Mindset', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Growth Mindset', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.navigatingStress,
          variant: {
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Stress / Anxiety', competencies)}`,
            isSubItem: true,
            type: 'NAVIGATION_ITEM',
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Stress / Anxiety', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.familyDynamics,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Family Dynamics', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Family Dynamics', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.sleepRestoration,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Sleep / Restoration', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Sleep / Restoration', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.seekingSupport,
          variant: {
            type: 'NAVIGATION_ITEM',
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Seeking Support', competencies)}`,
            isSubItem: true,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Seeking Support', competencies)}`,
              ),
          },
        },
        {
          title: MIDDLE_SECTION_TITLES.problemSolving,
          variant: {
            type: 'NAVIGATION_ITEM',
            isSubItem: true,
            isActive:
              location.pathname ===
              `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Problem Solving', competencies)}`,
            onClick: () =>
              navigate(
                `${APP_ROUTE.COMPETENCY}/${findCompetencySlug('Problem Solving', competencies)}`,
              ),
          },
        },
      ],
    },
  },
];

export const middleSection = ({
  navigate,
  location,
  dispatch,
  competencies,
  isUnauthenticated,
}: SectionDependencies): SidebarMenuItem[] => {
  const sectionHookProps = {
    navigate,
    location,
    dispatch,
    competencies,
    isUnauthenticated,
  };

  return [
    ...academicExpandable({ ...sectionHookProps }),
    ...careersExpandable({ ...sectionHookProps }),
    ...financesExpandable({ ...sectionHookProps }),
    ...healthAndSkillsExpandable({ ...sectionHookProps }),
  ];
};
